/**
 * auth.helpers.ts — pure helpers for offline-tolerant auth bootstrap (S4).
 *
 * Extracted as pure functions so they are testable without React rendering.
 */

import { HttpError } from "@/shared/lib/http-client.js";

/**
 * createFlushRefreshHandler — factory for the flush orchestrator's refresh handler.
 *
 * Returns a `() => Promise<void>` compatible with `setFlushRefreshHandler`.
 *
 * Behavior:
 *   - If no onRefresh is registered → resolves void (no-op, safe).
 *   - If onRefresh returns true → resolves void (token refreshed, flush may proceed).
 *   - If onRefresh returns false → resolves void (no current auth state; treat as
 *     offline/missing — no logout, outbox preserved).
 *   - If onRefresh THROWS HttpError(401 or 403) → the server explicitly rejected the
 *     refresh token → throws "session-expired" (flush aborts, outbox untouched).
 *   - If onRefresh THROWS any other error (TypeError / fetch failure / 5xx / timeout)
 *     → the server was unreachable; we cannot confirm expiry → resolves void (treat as
 *     offline: no toast, no logout, outbox preserved).
 *
 * KEY FIX: the old implementation used `navigator.onLine` to decide whether a failed
 * refresh meant "session expired". That was wrong: `navigator.onLine === true` does NOT
 * guarantee real connectivity (SW offline mode, weak signal, captive portal, DNS fail).
 * The correct signal is whether the server ACTUALLY RETURNED 401/403 (credential
 * rejection) vs the request never reaching the server (network-level failure).
 *
 * The caller (auth.context) provides a refresh fn that lets HttpError propagate —
 * unlike the http-client refresh fn (which catches everything and returns boolean) this
 * flush-specific variant throws on credential rejection so we can classify it here.
 *
 * ponytail: offline stale-token tolerance; ceiling = very long shift (refresh token
 * also expires); upgrade = surface login toast, keep outbox intact.
 *
 * @param getOnRefresh - getter returning the flush-specific refresh handler. The fn
 *   must let HttpError(401/403) propagate (not catch it); other errors may propagate
 *   too — they are treated as transient network failures.
 */
export function createFlushRefreshHandler(
  getOnRefresh: () => (() => Promise<boolean>) | null,
): () => Promise<void> {
  return async () => {
    const onRefresh = getOnRefresh();
    if (!onRefresh) return;

    try {
      await onRefresh();
      // true  → refreshed successfully, flush may proceed (no-op here).
      // false → no current auth state (caller returned early) → treat as offline.
    } catch (err) {
      if (err instanceof HttpError && (err.status === 401 || err.status === 403)) {
        // The server explicitly rejected the refresh token — credentials are invalid.
        throw new Error("session-expired");
      }
      // Network-level failure: fetch threw TypeError, server returned 5xx, timeout, etc.
      // We cannot confirm whether the refresh token is invalid if we never got a server
      // verdict. Treat as offline: silently resolve, keep outbox, no toast, no logout.
    }
  };
}

/**
 * shouldHydrateWithoutRefresh — determines if the cold-start bootstrap should
 * hydrate auth state WITHOUT calling refresh.
 *
 * Returns true when: offline AND a cached token exists in localStorage.
 * In that case: hydrate as-is (do NOT logout, do NOT call refresh).
 *
 * // ponytail: offline stale-token tolerance; ceiling = very long shift (refresh token
 * // also expires); upgrade = surface login toast, keep outbox intact
 */
export function shouldHydrateWithoutRefresh({
  isOnline,
  hasToken,
}: {
  isOnline: boolean;
  hasToken: boolean;
}): boolean {
  return !isOnline && hasToken;
}

/**
 * shouldWipeOnLogin — data isolation on shared devices.
 *
 * Returns true when a DIFFERENT account previously cached data on this device,
 * so the offline store (and sync anchor) must be wiped before loading the new
 * user's reports — otherwise stale reports leak, especially offline.
 *
 * Returns false when:
 *   - no previous user (first login on this device) → nothing to wipe.
 *   - same user re-login → keep the cache so offline work survives.
 */
export function shouldWipeOnLogin(prevUserId: string | null, newUserId: string): boolean {
  return Boolean(prevUserId) && prevUserId !== newUserId;
}
